import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { FolderKanban, Wallet } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import BackNavLink from "../../components/dashboard/BackNavLink";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { USE_MOCK_AUTH } from "../../config/api";
import { PORTFOLIO_ROWS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";
import {
  MOCK_CASES,
  MOCK_PORTFOLIO_GROUPS,
  formatCurrency,
  generateUploadRecords,
  getPortfolioBatchRows,
} from "../../data/mock/dashboardData";
import { getInstitutionById, getInstitutionCaseFilterName } from "../../data/institutionsStore";
import {
  fetchInstitutionGroupCases,
  fetchInstitutionPortfolioGroup,
  normalizeOpsCase,
  normalizeOpsGroup,
} from "../../services/institutionWorkspaceApi";
import { showError } from "../../utils/toast";
import { personNameColumns } from "../../utils/personName";

export default function InstitutionPortfolioGroupPage() {
  const { id, groupSlug } = useParams();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const liveCaseTick = useLiveEventTick(/^(portfolio\.|case\.|payment\.)/);
  const institution = getInstitutionById(id);
  const institutionName = institution ? getInstitutionCaseFilterName(institution) : "";

  const [group, setGroup] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const quiet = Boolean(liveCaseTick);
      if (!quiet) setLoading(true);
      try {
        if (!useLiveApi) {
          const mockGroup =
            getPortfolioBatchRows(MOCK_PORTFOLIO_GROUPS).find(
              (row) => row.id === groupSlug || row.group_slug === groupSlug
            ) ?? null;
          const mockUploads = (mockGroup?.uploads || []).map((upload) => ({
            ...upload,
            totalPaid: upload.totalPaid ?? Math.round((upload.totalOwed || 0) * 0.18),
            outstanding:
              upload.outstanding ?? Math.max(0, (upload.totalOwed || 0) - (upload.totalPaid || 0)),
          }));
          const uploadIds = new Set(mockUploads.map((upload) => upload.id));
          let mockCases = MOCK_CASES.filter(
            (row) => row.institution === (mockGroup?.institution || institutionName) || uploadIds.has(row.portfolioId)
          ).map((row) => ({
            ...row,
            outstandingAmount: Math.max(0, row.amountOwed - row.amountPaid),
            accountNumber: row.accountNumber || row.id,
          }));
          if (!mockCases.length && mockUploads[0]) {
            mockCases = generateUploadRecords(
              Math.min(mockUploads[0].accounts || 12, 25),
              mockUploads[0].id,
              mockGroup?.name
            ).map((row) =>
              normalizeOpsCase(
                {
                  ...row,
                  amount_owed: row.amount_owed,
                  amount_paid: row.amount_paid,
                },
                { institution: institutionName }
              )
            );
          }
          if (!cancelled) {
            setGroup(mockGroup);
            setCases(mockCases.filter(Boolean));
          }
          return;
        }

        const [groupRow, caseRows] = await Promise.all([
          fetchInstitutionPortfolioGroup(token, id, groupSlug, { force: Boolean(liveCaseTick) }),
          fetchInstitutionGroupCases(token, id, groupSlug, { force: Boolean(liveCaseTick) }),
        ]);
        if (cancelled) return;
        setGroup(normalizeOpsGroup(groupRow, institution?.name));
        setCases(
          (caseRows || [])
            .map((row) => normalizeOpsCase(row, { institution: institution?.name }))
            .filter(Boolean)
        );
      } catch (error) {
        if (!cancelled) {
          showError(error.message || "Could not load portfolio group.");
          setGroup(null);
          setCases([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [groupSlug, id, token, useLiveApi, liveCaseTick]);

  const owed = cases.reduce((sum, row) => sum + (Number(row.amountOwed) || 0), 0);
  const paid = cases.reduce((sum, row) => sum + (Number(row.amountPaid) || 0), 0);
  const outstanding = cases.reduce((sum, row) => sum + (Number(row.outstandingAmount) || 0), 0);

  const columns = useMemo(
    () => [
      { key: "numericId", header: "ID", type: "id" },
      { key: "id", header: "Case ID", type: "id" },
      ...personNameColumns(),
      { key: "accountNumber", header: "Account", type: "id" },
      { key: "outstandingAmount", header: "Outstanding", type: "currency" },
      { key: "phone", header: "Mobile", type: "phone" },
      { key: "customer_number", header: "Customer number", type: "phone" },
      { key: "email", header: "Email" },
      { key: "ghanaCard", header: "Ghana card" },
      { key: "gender", header: "Gender" },
      { key: "dateOfBirth", header: "Date of birth", type: "date" },
      { key: "country", header: "Country" },
      { key: "region", header: "Region" },
      { key: "exactArea", header: "Exact area" },
      { key: "gpsAddress", header: "GPS address" },
      { key: "loanAmount", header: "Loan amount", type: "currency" },
      { key: "amountOwed", header: "Amount owed", type: "currency" },
      { key: "amountPaid", header: "Amount paid", type: "currency" },
      { key: "dueDate", header: "Due date", type: "date" },
      { key: "lastPaymentDate", header: "Last payment", type: "date" },
      { key: "daysOverdue", header: "Days overdue", type: "number" },
    ],
    []
  );

  if (!loading && !group) {
    return (
      <PageShell
        title="Portfolio group"
        description="Group not found."
        actions={
          <BackNavLink
            fallback={`/dashboard/institutions/${id}/portfolio-groups`}
            fallbackLabel="All groups"
          />
        }
      >
        <p className="text-sm text-primary/50">No portfolio group matches {groupSlug}.</p>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={group?.name || "Portfolio group"}
      description={`${institution?.name || institutionName || "Institution"} · uploaded portfolio balances`}
      actions={
        <BackNavLink
          fallback={`/dashboard/institutions/${id}/portfolio-groups`}
          fallbackLabel="All groups"
        />
      }
    >
      {loading ? (
        <div className="mb-3 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-3 text-xs text-primary/50">
          Loading group…
        </div>
      ) : null}

      <StatCardGrid cols={4}>
        <StatCard label="Cases" value={cases.length} icon={FolderKanban} tone="primary" />
        <StatCard label="Total owed" value={formatCurrency(owed || group?.totalOwed || 0)} icon={Wallet} tone="accent" />
        <StatCard label="Paid" value={formatCurrency(paid || group?.totalPaid || 0)} icon={Wallet} tone="emerald" />
        <StatCard label="Outstanding" value={formatCurrency(outstanding || group?.outstanding || 0)} icon={Wallet} tone="sky" />
      </StatCardGrid>

      <h2 className="mb-3 text-sm font-bold text-primary">Portfolio data</h2>
      <FilteredDataTable
        data={cases}
        columns={columns}
        config={PORTFOLIO_ROWS_FILTER_CONFIG}
        pageSize={12}
        maxVisibleColumns={6}
        emptyMessage={loading ? "Loading portfolio data…" : "No portfolio rows in this group yet."}
      />
    </PageShell>
  );
}
